import { SearchIcon } from '@heroicons/react/solid'
import { format, parseISO } from 'date-fns'
import { useMemo } from 'react'
import { useSortBy, useTable, useFilters, CellProps } from 'react-table'
import { Column } from 'react-table'
import { ExternalAuctionResult } from '../../utils/types'

// rebuild again

export const SearchColumnFilter = ({ column: { filterValue, setFilter } }) => {
  return (
    <div className="my-2 mb-4 relative rounded-md shadow-sm w-full sm:w-96">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
      </div>
      <input
        value={filterValue || ''}
        onChange={(e) => {
          setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
        }}
        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 bg-white"
        placeholder="Search auctions"
      />
    </div>
  )
}

export const Table = ({ data }: { data: ExternalAuctionResult[] }) => {
  const columns = useMemo<Column<ExternalAuctionResult>[]>(
    () => [
      {
        Header: 'Auction',
        accessor: (row) => row.attributes.auctionTitle,
        // @ts-ignore
        Filter: SearchColumnFilter,
        className: 'col-span-3',
        Cell: ({ cell: { value } }) => {
          return <p className="text-gray-800 text-base">{value}</p>
        }
      },
      {
        Header: 'Date',
        accessor: (row) => row.attributes.auctionDate,
        disableFilters: true,
        className: 'col-span-1',
        // @ts-ignore
        Cell: ({ cell: { value } }) => {
          try {
            return (
              <>
                <div>
                  <label className="block sm:hidden text-sm font-medium text-gray-700">
                    Date
                  </label>
                </div>
                <div>{format(parseISO(value), 'M/d/yyyy')}</div>
              </>
            )
          } catch {
            return '-'
          }
        }
      },
      {
        Header: 'Lots',
        accessor: (row) => row.attributes.totalLots,
        // @ts-ignore
        disableFilters: true,
        className: 'col-span-1',
        Cell: ({ cell: { value } }) => {
          return (
            <>
              <div>
                <label className="block sm:hidden text-sm font-medium text-gray-700">
                  Lots
                </label>
              </div>
              <div className="sm:text-right">{value}</div>
            </>
          )
        }
      },
      {
        Header: 'Sales',
        accessor: (row) => row.attributes.totalSales,
        // @ts-ignore
        disableFilters: true,
        className: 'col-span-1',
        Cell: ({ cell: { value } }) => {
          const currency = new Intl.NumberFormat('en-US', {
            currency: 'USD',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
            style: 'currency'
          }).format(value)
          return (
            <>
              <div>
                <label className="block sm:hidden text-sm font-medium text-gray-700">
                  Sales
                </label>
              </div>
              <div className="sm:text-right">{currency}</div>
            </>
          )
        }
      },
      {
        Header: '',
        id: 'blank-column',
        // @ts-ignore
        disableFilters: true,
        className: 'col-span-3',
        Cell: (props: CellProps<ExternalAuctionResult>) => {
          const auctionType = props.row.original.attributes.auctionType
          const auctionId = props.row.original.attributes.auctionId
          const url =
            auctionType === 'ShowStockPlanetAuctionResult'
              ? `https://ssponlinesales.com/auction/${auctionId}`
              : auctionType === 'WilloughbyAuctionResult'
              ? `https://wlivestock.com/auction/${auctionId}`
              : null
          return (
            <>
              {url && (
                <div className="whitespace-nowrap text-center sm:text-left">
                  <a
                    target="_blank"
                    href={url}
                    className="w-full justify-center inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    rel="noreferrer"
                  >
                    View Results
                  </a>
                </div>
              )}
            </>
          )
        }
      }
    ],
    []
  )
  {
    /* <button
        type="button"
        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Button text
      </button> */
  }
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: {
          // @ts-ignore
          sortBy: [
            {
              id: 'Date',
              desc: true
            }
          ]
        }
      },
      useFilters,
      useSortBy
    )

  const filterColumn = headerGroups[0].headers.find(
    (h) => h.Header === 'Auction'
  )
  const filter = <div>{filterColumn.render('Filter')}</div>

  return (
    <div className="flex flex-col">
      <div>{filter}</div>
      <div className="-my-2 overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow sm:overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table
              {...getTableProps()}
              className="min-w-full divide-y divide-gray-200"
            >
              <thead className="bg-white">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row, index) => {
                  prepareRow(row)
                  return (
                    <tr
                      key={row.id}
                      {...row.getRowProps()}
                      className={`grid grid-cols-3 sm:table-row sm:px-6 py-2 sm:py-4 text-sm text-gray-500 ${
                        index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                      }`}
                      style={{ minHeight: '66px' }}
                    >
                      {row.cells.map((cell) => {
                        return (
                          <td
                            key={`${row.id}-${cell.column.id}`}
                            {...cell.getCellProps()}
                            // @ts-ignore
                            className={`py-2 px-6 sm:py-4 text-sm text-gray-500 ${cell.column.className}`}
                          >
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
